const BASE = '/api';

function getToken() {
  return localStorage.getItem('token');
}

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

async function request(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: authHeaders(),
    body: body != null ? JSON.stringify(body) : undefined
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Request failed');
    err.data = data; // e.g. { index } from a bulk submit, to point at the bad line
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  clientLogin: (clientName, password) =>
    request('POST', '/auth/login', { clientName, password }),
  adminLogin: (password) =>
    request('POST', '/auth/admin', { password }),

  // Portal
  getMe: () => request('GET', '/portal/me'),
  getManagedAccounts: () => request('GET', '/portal/managed'),
  managedShiftMarkPaid: (fromClient, shiftId, clientMarkedPaid) =>
    request('PATCH', `/portal/managed-shifts/${fromClient}/${shiftId}/mark-paid`, { clientMarkedPaid }),
  updateClientNotes: (shiftId, clientNotes) =>
    request('PATCH', `/portal/notes/${shiftId}`, { clientNotes }),
  clientMarkPaid: (shiftId, clientMarkedPaid) =>
    request('PATCH', `/portal/shifts/${shiftId}/mark-paid`, { clientMarkedPaid }),
  commissionShiftMarkPaid: (fromClient, shiftId, clientMarkedPaid) =>
    request('PATCH', `/portal/commission-shifts/${fromClient}/${shiftId}/mark-paid`, { clientMarkedPaid }),

  // Portal – timings (view-only, no usernames)
  syncPortalLogs: () => request('POST', '/portal/sync-logs', {}),
  getPortalTimingOptions: () => request('GET', '/portal/timings/options'),
  getPortalTimings: (clientid, bookingType) => {
    const params = new URLSearchParams();
    if (clientid) params.set('clientid', clientid);
    if (bookingType) params.set('bookingType', bookingType);
    return request('GET', `/portal/timings?${params.toString()}`);
  },
  getPortalTimingDrops: (clientid) => request('GET', `/portal/timing-drops?clientid=${encodeURIComponent(clientid)}`),

  // Portal – active runs (what the bot is running right now)
  getPortalActiveRuns: () => request('GET', '/portal/active-runs'),

  // Portal – shift requests
  getPortalRequestOptions: () => request('GET', '/portal/requests/options'),
  getPortalRequests: () => request('GET', '/portal/requests'),
  createPortalRequest: (data) => request('POST', '/portal/requests', data),
  createPortalRequests: (requests) => request('POST', '/portal/requests/bulk', { requests }),
  updatePortalRequest: (id, data) => request('PATCH', `/portal/requests/${id}`, data),
  deletePortalRequest: (id) => request('DELETE', `/portal/requests/${id}`),

  // Portal – logs (scoped to the client's own properties)
  getPortalLogOptions: () => request('GET', '/portal/logs/options'),
  getPortalLogs: ({ clientid, user, start, end } = {}) => {
    const params = new URLSearchParams();
    if (clientid) params.set('clientid', clientid);
    if (user) params.set('user', user);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return request('GET', `/portal/logs${qs ? '?' + qs : ''}`);
  },
  getPortalLog: (id) => request('GET', `/portal/logs/${id}`),

  // Admin – clients
  getClients: () => request('GET', '/admin/clients'),
  createClient: (data) => request('POST', '/admin/clients', data),
  updateClient: (id, data) => request('PATCH', `/admin/clients/${id}`, data),
  switchBillingType: (id, data) => request('PATCH', `/admin/clients/${id}/switch-billing-type`, data),
  deleteClientPassword: (id) => request('DELETE', `/admin/clients/${id}/password`),

  // Admin – users collection
  listUsers: () => request('GET', '/admin/users'),
  findClientUsers: (name, usernames) => {
    const params = new URLSearchParams({ name });
    if (usernames && usernames.length) params.append('usernames', usernames.join(','));
    return request('GET', `/admin/users/find?${params.toString()}`);
  },
  updateUser: (username, data) => request('PATCH', `/admin/users/${username}`, data),
  updateAllUsersVersion: (version) => request('PATCH', '/admin/users/version/all', { version }),
  getAppUpdates: () => request('GET', '/admin/app-updates'),
  updateAppUpdate: (id, data) => request('PATCH', `/admin/app-updates/${id}`, data),
  deleteUser: (username) => request('DELETE', `/admin/users/${username}`),
  createUser: (data) => request('POST', '/admin/users', data),

  // Admin – bills
  getBills: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/admin/bills${qs ? '?' + qs : ''}`);
  },
  updateBill: (id, data) => request('PATCH', `/admin/bills/${id}`, data),
  recalculateBill: (id) => request('POST', `/admin/bills/${id}/recalculate`, {}),
  markShiftPaid: (billId, shiftId, paid) =>
    request('PATCH', `/admin/bills/${billId}/shifts/${shiftId}/pay`, { paid }),
  markShiftCanceled: (billId, shiftId, canceled) =>
    request('PATCH', `/admin/bills/${billId}/shifts/${shiftId}/cancel`, { canceled }),
  markShiftNoCharge: (billId, shiftId, noCharge) =>
    request('PATCH', `/admin/bills/${billId}/shifts/${shiftId}/no-charge`, { noCharge }),

  // Admin – payments
  manualPayment: (billId, amount, note) =>
    request('POST', '/admin/payments', { billId, amount, note }),
  groupPayment: (managerClientName, amount, note) =>
    request('POST', '/admin/payments/group', { managerClientName, amount, note }),

  // Admin – unidentified shifts
  getUnidentified: () => request('GET', '/admin/unidentified'),
  assignTag: (billId, shiftId, rawTag) =>
    request('PATCH', `/admin/shifts/${shiftId}/tag`, { billId, shiftId, rawTag }),

  // Admin – notes
  updateAdminNotes: (shiftId, adminNotes) =>
    request('PATCH', `/admin/notes/${shiftId}`, { adminNotes }),

  // Admin – commissions
  getCommissions: () => request('GET', '/admin/commissions'),
  updateCommission: (id, data) => request('PATCH', `/admin/commissions/${id}`, data),
  settleCommission: (id, settled) => request('PATCH', `/admin/commissions/${id}/settle`, { settled }),

  // Admin – pricing
  getPricing: (clientName) => request('GET', `/admin/pricing/${clientName}`),
  updatePricing: (clientName, data) => request('PATCH', `/admin/pricing/${clientName}`, data),

  // Admin – preshift
  getPreshift: (clientid) => {
    const params = new URLSearchParams();
    if (clientid) params.set('clientid', clientid);
    return request('GET', `/admin/preshift?${params.toString()}`);
  },

  // Admin – timings
  syncLogs: () => request('POST', '/admin/sync-logs', {}),
  getTimingDrops: (clientid) => request('GET', `/admin/timing-drops?clientid=${encodeURIComponent(clientid)}`),
  upsertTimingDrop: (clientid, date, bucket, availableShifts, photoUrl) => request('PUT', '/admin/timing-drops', { clientid, date, bucket, availableShifts, ...(photoUrl !== undefined && { photoUrl }) }),
  uploadTimingPhoto: async (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const res = await fetch('/api/admin/timing-drops/upload', {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },
  getTimingOptions: () => request('GET', '/admin/timings/options'),
  getTimings: (clientid, bookingType) => {
    const params = new URLSearchParams();
    if (clientid) params.set('clientid', clientid);
    if (bookingType) params.set('bookingType', bookingType);
    return request('GET', `/admin/timings?${params.toString()}`);
  },
  deleteTiming: (id) => request('DELETE', `/admin/timings/${id}`),

  // Admin – logs
  getLogOptions: () => request('GET', '/admin/logs/options'),
  getLogs: ({ clientName, clientid, user, start, end } = {}) => {
    const params = new URLSearchParams();
    if (clientName) params.set('clientName', clientName);
    if (clientid) params.set('clientid', clientid);
    if (user) params.set('user', user);
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return request('GET', `/admin/logs${qs ? '?' + qs : ''}`);
  },
  getLog: (id) => request('GET', `/admin/logs/${id}`),

  // Admin – active runs
  getActiveRunOptions: () => request('GET', '/admin/active-runs/options'),
  getActiveRuns: ({ clientID, userName, status } = {}) => {
    const params = new URLSearchParams();
    if (clientID) params.set('clientID', clientID);
    if (userName) params.set('userName', userName);
    if (status) params.set('status', status);
    const qs = params.toString();
    return request('GET', `/admin/active-runs${qs ? '?' + qs : ''}`);
  },

  // Admin – shift requests
  getShiftRequests: (status) => request('GET', `/admin/requests${status ? `?status=${status}` : ''}`),
  setShiftRequestRunning: (id, running) => request('PATCH', `/admin/requests/${id}/running`, { running }),
  deleteShiftRequest: (id) => request('DELETE', `/admin/requests/${id}`),

  // Admin – bill generation
  generateBill: () =>
    request('POST', '/admin/generate-bill', {}),

  // Admin – zelle mappings
  getZelleMappings: () => request('GET', '/admin/zelle-mappings'),
  createZelleMapping: (data) => request('POST', '/admin/zelle-mappings', data),
  deleteZelleMapping: (id) => request('DELETE', `/admin/zelle-mappings/${id}`),

  // Admin – email
  sendEmail: (to, subject, message) => request('POST', '/admin/send-email', { to, subject, message }),

  // Payment reminders
  getReminderPreview: (id) => request('GET', `/admin/clients/${id}/reminder-preview`),
  sendReminderNow: (id) => request('POST', `/admin/clients/${id}/send-reminder`),

  // Admin – fleet / remote control of the booking bots
  getSlots: () => request('GET', '/admin/slots'),
  // scope 'machine' (all ports) or 'port' (+ instanceId); ownerType 'inherit' clears a port's own owner.
  setDeviceBinding: (machineId, ownerType, portalClientId, scope = 'machine', instanceId) =>
    request('PUT', '/admin/device-bindings', { machineId, ownerType, portalClientId, scope, instanceId }),
  getBotProfiles: () => request('GET', '/admin/bot-profiles'),
  createBotProfile: (data) => request('POST', '/admin/bot-profiles', data),
  bulkBotProfiles: (profiles) => request('POST', '/admin/bot-profiles/bulk', { profiles }),
  updateBotProfile: (id, data) => request('PATCH', `/admin/bot-profiles/${id}`, data),
  deleteBotProfile: (id) => request('DELETE', `/admin/bot-profiles/${id}`),
  revealBotProfile: (id) => request('GET', `/admin/bot-profiles/${id}/credentials`),
  getChromeProfiles: (machineId) =>
    request('GET', `/admin/chrome-profiles${machineId ? `?machineId=${encodeURIComponent(machineId)}` : ''}`),
  createChromeProfile: (data) => request('POST', '/admin/chrome-profiles', data),
  deleteChromeProfile: (id) => request('DELETE', `/admin/chrome-profiles/${id}`),
  startFleetRun: (data) => request('POST', '/admin/runs', data),
  stopFleetRun: (instanceId) => request('POST', '/admin/runs/stop', { instanceId }),
  updateFleetSlot: (instanceId, channel) => request('POST', '/admin/runs/update', { instanceId, channel }),
  forgetFleetSlot: (instanceId) => request('DELETE', `/admin/known-slots/${encodeURIComponent(instanceId)}`),
  getFleetRuns: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/admin/runs${qs ? '?' + qs : ''}`);
  },
  getFleetLiveLog: (params) => request('GET', `/admin/live-log?${new URLSearchParams(params).toString()}`),

  // Portal – client self-serve fleet (scoped to the client's own machines/profiles)
  getPortalSlots: () => request('GET', '/portal/fleet/slots'),
  getPortalBotProfiles: () => request('GET', '/portal/fleet/bot-profiles'),
  createPortalBotProfile: (data) => request('POST', '/portal/fleet/bot-profiles', data),
  bulkPortalBotProfiles: (profiles) => request('POST', '/portal/fleet/bot-profiles/bulk', { profiles }),
  updatePortalBotProfile: (id, data) => request('PATCH', `/portal/fleet/bot-profiles/${id}`, data),
  deletePortalBotProfile: (id) => request('DELETE', `/portal/fleet/bot-profiles/${id}`),
  revealPortalBotProfile: (id) => request('GET', `/portal/fleet/bot-profiles/${id}/credentials`),
  createPortalChromeProfile: (data) => request('POST', '/portal/fleet/chrome-profiles', data),
  deletePortalChromeProfile: (id) => request('DELETE', `/portal/fleet/chrome-profiles/${id}`),
  startPortalRun: (data) => request('POST', '/portal/fleet/runs', data),
  stopPortalRun: (instanceId) => request('POST', '/portal/fleet/runs/stop', { instanceId }),
  getPortalRuns: () => request('GET', '/portal/fleet/runs'),
  getPortalLiveLog: (params) => request('GET', `/portal/fleet/live-log?${new URLSearchParams(params).toString()}`)
};
